import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Class,
  Assignment,
  Assessment,
  Person,
  Logout,
  Settings,
  ContactPhone,
  Event,
  PersonAdd,
  Store,
  VideoLibrary,
  Schedule,
  MenuBook,
  Chat,
  Paid,
  CompareArrows,
  CalendarMonth,
  ExpandLess,
  ExpandMore,
  TrendingUp,
  BarChart,
  WarningAmber,
  School,
  HowToReg,
  EventBusy,
  EditNote,
  Description,
  RateReview,
  Tune,
  FactCheck,
  Groups,
  AccessTime,
  ConfirmationNumber,
  HelpOutline,
  SupportAgent,
  Newspaper,
  History,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { SupervisorTeacherProvider, useSupervisorTeacherContext } from '../../contexts/SupervisorTeacherContext';
import NotificationBell from '../Notifications/NotificationBell';
import BrandLogo from '../BrandLogo';
import { whatsappAPI } from '../../services/api';
import CallingHost from '../Calling/CallingHost';

const drawerWidth = 260;

const LayoutShell = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout, isAdmin, isTeacher, isTeacherAdmin, isTeachingLead, isSales, isCountryManager, isSalesCountryScope, isFinanceOrAdmin, isParentFeedbackAgent, isBatchOps, hasSalesSupervisorCountryAccess } = useAuth();
  const supervisorPicker = useSupervisorTeacherContext();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const whatsappUnread = useQuery(
    ['whatsappUnread'],
    async () => {
      const res = await whatsappAPI.unreadCount();
      return res.data?.unread_count || 0;
    },
    {
      enabled: isAdmin() || isSales(),
      refetchInterval: 30000,
      refetchIntervalInBackground: false,
      retry: false,
    }
  );
  const whatsappBadge = whatsappUnread.data || 0;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout();
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const isReportsPath = (pathname) =>
    pathname === '/management'
    || pathname.startsWith('/management/')
    || pathname === '/reports'
    || pathname.startsWith('/teacher/student-attendance')
    ||     pathname === '/leads/analytics';

  const isSupervisorMyClassesPath = (pathname) =>
    pathname === '/teacher/home'
    || pathname.startsWith('/teacher/timetable')
    || pathname.startsWith('/teacher/upcoming-batches')
    || pathname.startsWith('/teacher/makeup-classes')
    || pathname.startsWith('/teacher/substitution-history')
    || pathname.startsWith('/teacher/student-attendance')
    || pathname.startsWith('/admission/student-attendance')
    || pathname === '/teacher/leave'
    || pathname === '/leave';

  const isSupervisorFillReportsPath = (pathname) =>
    pathname === '/teacher/monthly-report'
    || pathname.startsWith('/teacher/what-was-taught')
    || pathname.startsWith('/teacher/student-reports');

  const isSupervisorReviewReportsPath = (pathname) =>
    pathname.startsWith('/teacher/monthly-reports-inbox');

  const isSupervisorApprovalsPath = (pathname) =>
    pathname.startsWith('/teacher/leave-approvals')
    || pathname.startsWith('/teacher/approvals');

  const isSupervisorTeamPath = (pathname) =>
    pathname === '/batches'
    || pathname.startsWith('/batches/')
    || pathname === '/batch-options'
    || pathname.startsWith('/batch-options/')
    || pathname === '/teachers'
    || pathname.startsWith('/teachers/')
    || pathname === '/students'
    || pathname.startsWith('/students/')
    || pathname === '/demos'
    || pathname.startsWith('/demos/')
    || pathname === '/allocations'
    || pathname.startsWith('/allocations/')
    || pathname === '/scheduling'
    || pathname.startsWith('/scheduling/')
    || pathname === '/teacher/supervisor-roster';

  const isSupervisorTrainingPath = (pathname) => pathname.startsWith('/lms/');

  const matchNavPath = (pathname, itemPath) => pathname === itemPath;

  const resolveActiveNavItem = (items, pathname) =>
    items
      .filter((item) => matchNavPath(pathname, item.path))
      .sort((a, b) => b.path.length - a.path.length)[0];

  // Navigation items based on user role
  const getNavigationItems = () => {
    // Base always-visible item
    const dashboardItem = {
      type: 'item',
      text: 'Dashboard',
      icon: <Dashboard />,
      path: '/dashboard',
    };

    // Finance (admin or finance_user, finance_supervisor, finance_admin): show Finance menu
    const financeGroup = {
      type: 'group',
      id: 'finance',
      text: 'Finance',
      icon: <Assessment />,
      children: [
        { type: 'item', text: 'Subscriptions', icon: <Assessment />, path: '/finance' },
        { type: 'item', text: 'Fees & Pricing', icon: <Paid />, path: '/finance/pricing' },
      ],
    };

    // Admin: full grouped menu
    if (isAdmin()) {
      return [
        dashboardItem,
        financeGroup,
        {
          type: 'group',
          id: 'payroll',
          text: 'Payroll',
          icon: <Paid />,
          children: [
            { type: 'item', text: 'Staff profiles', icon: <People />, path: '/payroll/people' },
            { type: 'item', text: 'Pay setup', icon: <Paid />, path: '/payroll/setup' },
            { type: 'item', text: 'Leave and holidays', icon: <EventBusy />, path: '/leave/setup' },
            { type: 'item', text: 'Payroll', icon: <Paid />, path: '/payroll' },
            { type: 'item', text: 'Timesheet inbox', icon: <AccessTime />, path: '/timesheets/inbox' },
            { type: 'item', text: 'Leave to approve', icon: <EventBusy />, path: '/leave/inbox' },
          ],
        },
        {
          type: 'group',
          id: 'lms',
          text: 'LMS',
          icon: <VideoLibrary />,
          children: [
            { type: 'item', text: 'LMS Content', icon: <VideoLibrary />, path: '/lms/content' },
            { type: 'item', text: 'LMS Assignments', icon: <Assignment />, path: '/lms/assignments' },
            { type: 'item', text: 'Staff meetings', icon: <Event />, path: '/meetings' },
            { type: 'item', text: 'My Training', icon: <VideoLibrary />, path: '/lms/my-content' },
          ],
        },
        {
          type: 'group',
          id: 'teaching',
          text: 'Teaching',
          icon: <MenuBook />,
          children: [
            { type: 'item', text: 'Batches', icon: <Class />, path: '/batches' },
            { type: 'item', text: 'Class observations', icon: <FactCheck />, path: '/teaching/class-observations' },
            { type: 'item', text: 'Batch Options', icon: <Class />, path: '/batch-options' },
            { type: 'item', text: 'Teachers', icon: <PersonAdd />, path: '/teachers' },
            { type: 'item', text: 'Students', icon: <People />, path: '/students' },
            { type: 'item', text: 'Leave requests', icon: <Schedule />, path: '/teacher/leave-approvals' },
          ],
        },
        {
          type: 'group',
          id: 'leads',
          text: 'Leads',
          icon: <ContactPhone />,
          children: [
            { type: 'item', text: 'Sales Users', icon: <Store />, path: '/sales' },
            { type: 'item', text: 'Demos', icon: <Event />, path: '/demos' },
            { type: 'item', text: 'Leads', icon: <ContactPhone />, path: '/leads' },
            { type: 'item', text: 'RM students', icon: <HowToReg />, path: '/rm' },
            { type: 'item', text: 'Leave follow-ups', icon: <EventBusy />, path: '/rm/leave-followups' },
            { type: 'item', text: 'Parent meetings', icon: <Event />, path: '/rm/parent-meetings' },
            { type: 'item', text: 'Parent meeting calendar', icon: <CalendarMonth />, path: '/rm/parent-meetings/calendar' },
          ],
        },
        { type: 'item', text: 'WhatsApp', icon: <Chat />, path: '/whatsapp', badge: whatsappBadge },
        {
          type: 'item',
          text: 'Scheduling',
          icon: <Schedule />,
          path: '/scheduling',
        },
        {
          type: 'item',
          text: 'Allocations',
          icon: <Assignment />,
          path: '/allocations',
        },
        {
          type: 'group',
          id: 'reports',
          text: 'Reports',
          icon: <Assessment />,
          children: [
            { type: 'item', text: 'Sales & Enrollment', icon: <TrendingUp />, path: '/management' },
            { type: 'item', text: 'Rankings', icon: <BarChart />, path: '/management/rankings' },
            { type: 'item', text: 'Stuck Leads', icon: <WarningAmber />, path: '/management/stuck-leads' },
            { type: 'item', text: 'Teacher Reports', icon: <School />, path: '/reports' },
            { type: 'item', text: 'Student attendance', icon: <FactCheck />, path: '/teacher/student-attendance' },
            { type: 'item', text: 'Leads Analytics', icon: <ContactPhone />, path: '/leads/analytics' },
            { type: 'item', text: 'Tickets', icon: <ConfirmationNumber />, path: '/tickets' },
            { type: 'item', text: 'Ticket inbox', icon: <ConfirmationNumber />, path: '/tickets/inbox' },
            { type: 'item', text: 'Family messages', icon: <Chat />, path: '/teacher/family-messages' },
          ],
        },
        {
          type: 'item',
          text: 'Users',
          icon: <People />,
          path: '/admin/users',
        },
      ];
    }

    // Finance-only users (no admin): Dashboard + Finance, then payroll in the order the work is done
    if (isFinanceOrAdmin()) {
      const canRunPayMonth = user?.role === 'finance_supervisor' || user?.role === 'finance_admin';
      const canMaintainPay = user?.role === 'finance_supervisor';
      const canApproveStaff = user?.role === 'finance_supervisor' || user?.role === 'finance_admin';
      const payrollChildren = [
        ...(canMaintainPay
          ? [
              { type: 'item', text: 'Staff profiles', icon: <People />, path: '/payroll/people' },
              { type: 'item', text: 'Pay setup', icon: <Paid />, path: '/payroll/setup' },
              { type: 'item', text: 'Leave and holidays', icon: <EventBusy />, path: '/leave/setup' },
            ]
          : []),
        ...(canRunPayMonth
          ? [{ type: 'item', text: 'Payroll', icon: <Paid />, path: '/payroll' }]
          : []),
      ];
      return [
        dashboardItem,
        financeGroup,
        ...(payrollChildren.length
          ? [{
              type: 'group',
              id: 'payroll',
              text: 'Payroll',
              icon: <Paid />,
              children: payrollChildren,
            }]
          : []),
        ...(canApproveStaff
          ? [{
              type: 'group',
              id: 'payrollApprovals',
              text: 'To approve',
              icon: <RateReview />,
              children: [
                { type: 'item', text: 'Timesheet inbox', icon: <RateReview />, path: '/timesheets/inbox' },
                { type: 'item', text: 'Leave to approve', icon: <EventBusy />, path: '/leave/inbox' },
              ],
            }]
          : []),
        {
          type: 'group',
          id: 'myWork',
          text: 'My work',
          icon: <AccessTime />,
          children: [
            { type: 'item', text: 'Timesheet', icon: <AccessTime />, path: '/timesheets' },
            { type: 'item', text: 'My leave', icon: <EventBusy />, path: '/leave' },
            { type: 'item', text: 'My payslip', icon: <Assessment />, path: '/payroll/me' },
          ],
        },
        ...(canMaintainPay
          ? [{
              type: 'group',
              id: 'students',
              text: 'Students',
              icon: <People />,
              children: [
                { type: 'item', text: 'Student attendance', icon: <FactCheck />, path: '/teacher/student-attendance' },
                { type: 'item', text: 'Students', icon: <People />, path: '/students' },
              ],
            }]
          : []),
        ...(user?.role === 'finance_admin'
          ? [
              { type: 'item', text: 'Tickets', icon: <ConfirmationNumber />, path: '/tickets' },
              { type: 'item', text: 'Ticket inbox', icon: <ConfirmationNumber />, path: '/tickets/inbox' },
            ]
          : []),
      ];
    }

    // Sales users (non-admin) – keep flat but relevant
    if (isSales()) {
      return [
        dashboardItem,
        { type: 'item', text: 'Leads', icon: <ContactPhone />, path: '/leads' },
        { type: 'item', text: 'WhatsApp', icon: <Chat />, path: '/whatsapp', badge: whatsappBadge },
        { type: 'item', text: 'Leads Analytics', icon: <Assessment />, path: '/leads/analytics' },
        { type: 'item', text: 'Demos', icon: <Event />, path: '/demos' },
        { type: 'item', text: 'Timesheet', icon: <AccessTime />, path: '/timesheets' },
        { type: 'item', text: 'My leave', icon: <EventBusy />, path: '/leave' },
        { type: 'item', text: 'My payslip', icon: <Assessment />, path: '/payroll/me' },
        ...(isSalesCountryScope()
          ? [
              { type: 'item', text: 'Timesheet inbox', icon: <RateReview />, path: '/timesheets/inbox' },
              { type: 'item', text: 'Leave to approve', icon: <EventBusy />, path: '/leave/inbox' },
            ]
          : []),
        { type: 'item', text: 'Tickets', icon: <ConfirmationNumber />, path: '/tickets' },
        { type: 'item', text: 'Ticket inbox', icon: <ConfirmationNumber />, path: '/tickets/inbox' },
        { type: 'item', text: 'Family messages', icon: <Chat />, path: '/rm/family-messages' },
        { type: 'item', text: 'Scheduling', icon: <Schedule />, path: '/scheduling' },
        { type: 'item', text: 'My students', icon: <HowToReg />, path: '/admission' },
        { type: 'item', text: 'RM students', icon: <HowToReg />, path: '/rm' },
        { type: 'item', text: 'Leave follow-ups', icon: <EventBusy />, path: '/rm/leave-followups' },
        { type: 'item', text: 'Parent meetings', icon: <Event />, path: '/rm/parent-meetings' },
        { type: 'item', text: 'Parent meeting calendar', icon: <CalendarMonth />, path: '/rm/parent-meetings/calendar' },
        {
          type: 'item',
          text: isCountryManager() ? 'Review & send to parents' : 'Monthly reports',
          icon: <RateReview />,
          path: '/teacher/monthly-reports-inbox',
        },
        { type: 'item', text: 'Batches', icon: <Class />, path: '/batches' },
        { type: 'item', text: 'Batch Options', icon: <Class />, path: '/batch-options' },
        ...(isCountryManager()
          ? [{ type: 'item', text: 'Teachers', icon: <PersonAdd />, path: '/teachers' }]
          : []),
        ...(isCountryManager()
          ? [
              { type: 'item', text: 'Teaching home', icon: <Dashboard />, path: '/teacher/home' },
              { type: 'item', text: 'Makeup classes',
                icon: <CompareArrows />,
                path: '/teacher/makeup-classes',
              },
              { type: 'item', text: 'Substitution history', icon: <History />, path: '/teacher/substitution-history' },
              { type: 'item', text: 'Leave & substitutes', icon: <Schedule />, path: '/teacher/leave-approvals' },
            ]
          : []),
        ...(isCountryManager()
          ? [
              {
                type: 'group',
                id: 'admission',
                text: 'Admission',
                icon: <HowToReg />,
                children: [
                  { type: 'item', text: 'Student attendance', icon: <FactCheck />, path: '/admission/student-attendance' },
                ],
              },
            ]
          : []),
      ];
    }

    if (isParentFeedbackAgent()) {
      return [
        dashboardItem,
        { type: 'item', text: 'Students', icon: <People />, path: '/students' },
        {
          type: 'item',
          text: 'Monthly reports',
          icon: <RateReview />,
          path: '/teacher/monthly-reports-inbox',
        },
        { type: 'item', text: 'Timesheet', icon: <AccessTime />, path: '/timesheets' },
        { type: 'item', text: 'My leave', icon: <EventBusy />, path: '/leave' },
        { type: 'item', text: 'My payslip', icon: <Assessment />, path: '/payroll/me' },
        { type: 'item', text: 'Tickets', icon: <ConfirmationNumber />, path: '/tickets' },
      ];
    }

    if (isBatchOps()) {
      return [
        dashboardItem,
        { type: 'item', text: 'Batches', icon: <Class />, path: '/batches' },
        { type: 'item', text: 'Batch options', icon: <Class />, path: '/batch-options' },
        { type: 'item', text: 'Teachers', icon: <PersonAdd />, path: '/teachers' },
        { type: 'item', text: 'Students', icon: <People />, path: '/students' },
        { type: 'item', text: 'Allocations', icon: <Assignment />, path: '/allocations' },
        { type: 'item', text: 'Scheduling', icon: <Schedule />, path: '/scheduling' },
        { type: 'item', text: 'Timesheet', icon: <AccessTime />, path: '/timesheets' },
        { type: 'item', text: 'My leave', icon: <EventBusy />, path: '/leave' },
        { type: 'item', text: 'My payslip', icon: <Assessment />, path: '/payroll/me' },
        { type: 'item', text: 'Tickets', icon: <ConfirmationNumber />, path: '/tickets' },
        { type: 'item', text: 'Ticket inbox', icon: <ConfirmationNumber />, path: '/tickets/inbox' },
      ];
    }

    // Teacher supervisor / teacher admin (non-platform-admin): grouped by daily work vs team oversight
    if (isTeachingLead() && !isAdmin()) {
      return [
        dashboardItem,
        { type: 'item', text: 'Timesheet', icon: <AccessTime />, path: '/timesheets' },
        { type: 'item', text: 'Leave to approve', icon: <EventBusy />, path: '/leave/inbox' },
        {
          type: 'group',
          id: 'myClasses',
          text: 'My classes',
          icon: <CalendarMonth />,
          children: [
            { type: 'item', text: 'Timetable', icon: <CalendarMonth />, path: '/teacher/timetable' },
            { type: 'item', text: 'My batches', icon: <Class />, path: '/teacher/upcoming-batches' },
            { type: 'item', text: 'Student attendance', icon: <FactCheck />, path: '/teacher/student-attendance' },
            { type: 'item', text: 'Makeup classes', icon: <CompareArrows />, path: '/teacher/makeup-classes' },
            { type: 'item', text: 'Substitution history', icon: <History />, path: '/teacher/substitution-history' },
            { type: 'item', text: 'Class leave', icon: <Schedule />, path: '/teacher/leave' },
            { type: 'item', text: 'Pay leave', icon: <EventBusy />, path: '/leave' },
            { type: 'item', text: 'My payslip', icon: <Assessment />, path: '/payroll/me' },
          ],
        },
        {
          type: 'group',
          id: 'myMonthlyReports',
          text: 'My monthly reports',
          icon: <EditNote />,
          children: [
            {
              type: 'item',
              text: 'My achievement report',
              icon: <Description />,
              path: '/teacher/monthly-report',
            },
            {
              type: 'item',
              text: 'What students were taught',
              icon: <EditNote />,
              path: '/teacher/what-was-taught',
            },
            {
              type: 'item',
              text: 'My student assessments',
              icon: <EditNote />,
              path: '/teacher/student-reports',
            },
          ],
        },
        {
          type: 'group',
          id: 'teamMonthlyReports',
          text: 'Team monthly reports',
          icon: <RateReview />,
          children: [
            {
              type: 'item',
              text: 'Review & send to parents',
              icon: <RateReview />,
              path: '/teacher/monthly-reports-inbox',
            },
            {
              type: 'item',
              text: 'Timesheet inbox',
              icon: <AccessTime />,
              path: '/timesheets/inbox',
            },
            {
              type: 'item',
              text: 'Tickets',
              icon: <ConfirmationNumber />,
              path: '/tickets',
            },
            {
              type: 'item',
              text: 'Ticket inbox',
              icon: <ConfirmationNumber />,
              path: '/tickets/inbox',
            },
            {
              type: 'item',
              text: 'Family messages',
              icon: <Chat />,
              path: '/teacher/family-messages',
            },
            ...(isTeacherAdmin()
              ? [
                  {
                    type: 'item',
                    text: 'Assessment form setup',
                    icon: <Tune />,
                    path: '/teacher/assessment-catalog',
                  },
                  {
                    type: 'item',
                    text: 'Trainer performance',
                    icon: <TrendingUp />,
                    path: '/management',
                  },
                ]
              : []),
          ],
        },
        {
          type: 'group',
          id: 'supervisorApprovals',
          text: 'Approve requests',
          icon: <FactCheck />,
          children: [
            { type: 'item', text: 'Leave requests', icon: <Schedule />, path: '/teacher/leave-approvals' },
            { type: 'item', text: 'Session requests', icon: <FactCheck />, path: '/teacher/approvals' },
          ],
        },
        {
          type: 'group',
          id: 'teamManagement',
          text: 'Batches & team',
          icon: <Groups />,
          children: [
            { type: 'item', text: 'Batches', icon: <Class />, path: '/batches' },
            { type: 'item', text: 'Class observations', icon: <FactCheck />, path: '/teaching/class-observations' },
            { type: 'item', text: 'Batch options', icon: <Class />, path: '/batch-options' },
            { type: 'item', text: 'Teachers', icon: <PersonAdd />, path: '/teachers' },
            ...(isTeacherAdmin()
              ? [{ type: 'item', text: 'Supervisor roster', icon: <Groups />, path: '/teacher/supervisor-roster' }]
              : []),
            { type: 'item', text: 'Students', icon: <People />, path: '/students' },
            { type: 'item', text: 'Demos', icon: <Event />, path: '/demos' },
            { type: 'item', text: 'Allocations', icon: <Assignment />, path: '/allocations' },
            { type: 'item', text: 'Scheduling', icon: <Schedule />, path: '/scheduling' },
          ],
        },
        {
          type: 'group',
          id: 'trainingContent',
          text: 'Training & content',
          icon: <VideoLibrary />,
          children: [
            { type: 'item', text: 'LMS content', icon: <VideoLibrary />, path: '/lms/content' },
            { type: 'item', text: 'LMS assignments', icon: <Assignment />, path: '/lms/assignments' },
            { type: 'item', text: 'Staff meetings', icon: <Event />, path: '/meetings' },
            { type: 'item', text: 'My meetings', icon: <Event />, path: '/meetings/mine' },
            { type: 'item', text: 'My training', icon: <VideoLibrary />, path: '/lms/my-content' },
            { type: 'item', text: 'Class content', icon: <MenuBook />, path: '/lms/class-content' },
          ],
        },
        {
          type: 'group',
          id: 'sessionCompliance',
          text: 'Session compliance',
          icon: <School />,
          children: [
            { type: 'item', text: 'Teacher session reports', icon: <School />, path: '/reports' },
          ],
        },
      ];
    }

    // Teachers (classroom tools only)
    if (isTeacher()) {
      return [
        dashboardItem,
        {
          type: 'group',
          id: 'myClasses',
          text: 'My classes',
          icon: <CalendarMonth />,
          children: [
            { type: 'item', text: 'Timetable', icon: <CalendarMonth />, path: '/teacher/timetable' },
            { type: 'item', text: 'My batches', icon: <Class />, path: '/teacher/upcoming-batches' },
            { type: 'item', text: 'Student attendance', icon: <FactCheck />, path: '/teacher/student-attendance' },
            { type: 'item', text: 'Demos', icon: <Event />, path: '/demos' },
            { type: 'item', text: 'Makeup classes', icon: <CompareArrows />, path: '/teacher/makeup-classes' },
            { type: 'item', text: 'Substitution history', icon: <History />, path: '/teacher/substitution-history' },
            { type: 'item', text: 'Class leave', icon: <Schedule />, path: '/teacher/leave' },
            { type: 'item', text: 'Pay leave', icon: <EventBusy />, path: '/leave' },
            { type: 'item', text: 'My payslip', icon: <Assessment />, path: '/payroll/me' },
            { type: 'item', text: 'Timesheet', icon: <AccessTime />, path: '/timesheets' },
          ],
        },
        {
          type: 'group',
          id: 'myMonthlyReports',
          text: 'My monthly reports',
          icon: <EditNote />,
          children: [
            {
              type: 'item',
              text: 'My achievement report',
              icon: <Description />,
              path: '/teacher/monthly-report',
            },
            {
              type: 'item',
              text: 'What students were taught',
              icon: <EditNote />,
              path: '/teacher/what-was-taught',
            },
            {
              type: 'item',
              text: 'My student assessments',
              icon: <EditNote />,
              path: '/teacher/student-reports',
            },
          ],
        },
        {
          type: 'group',
          id: 'tickets',
          text: 'Tickets',
          icon: <ConfirmationNumber />,
          children: [
            { type: 'item', text: 'Tickets', icon: <ConfirmationNumber />, path: '/tickets' },
            { type: 'item', text: 'Ticket inbox', icon: <ConfirmationNumber />, path: '/tickets/inbox' },
            { type: 'item', text: 'Family messages', icon: <Chat />, path: '/teacher/family-messages' },
          ],
        },
        {
          type: 'group',
          id: 'trainingContent',
          text: 'Training & content',
          icon: <VideoLibrary />,
          children: [
            { type: 'item', text: 'My training', icon: <VideoLibrary />, path: '/lms/my-content' },
            { type: 'item', text: 'Class content', icon: <MenuBook />, path: '/lms/class-content' },
            { type: 'item', text: 'My meetings', icon: <Event />, path: '/meetings/mine' },
          ],
        },
      ];
    }

    // Default
    return [dashboardItem];
  };

  const navigationItems = (() => {
    const items = getNavigationItems();
    const helpItems = [
      { type: 'item', text: 'News & updates', icon: <Newspaper />, path: '/news' },
      { type: 'item', text: 'Help', icon: <HelpOutline />, path: '/help' },
    ];
    if (isAdmin()) {
      helpItems.push({
        type: 'item',
        text: 'Help Admin',
        icon: <SupportAgent />,
        path: '/help/admin',
      });
    }
    return [...items, ...helpItems];
  })();

  const flattenNavigationItems = (items) => {
    const result = [];
    items.forEach((item) => {
      if (item.type === 'item') {
        result.push(item);
      } else if (item.type === 'group' && Array.isArray(item.children)) {
        result.push(...item.children);
      }
    });
    return result;
  };

  const flatItems = flattenNavigationItems(navigationItems);

  const [openGroups, setOpenGroups] = useState({
    finance: false,
    lms: false,
    teaching: false,
    leads: false,
    admission: false,
    reports: isReportsPath(location.pathname),
    payroll: location.pathname.startsWith('/payroll') || location.pathname.startsWith('/leave'),
    payrollApprovals: location.pathname.startsWith('/timesheets/inbox') || location.pathname.startsWith('/leave/inbox'),
    myWork: location.pathname === '/timesheets' || location.pathname === '/leave' || location.pathname.startsWith('/payroll/me'),
    myClasses: isSupervisorMyClassesPath(location.pathname),
    myMonthlyReports: isSupervisorFillReportsPath(location.pathname),
    teamMonthlyReports: isSupervisorReviewReportsPath(location.pathname),
    supervisorApprovals: isSupervisorApprovalsPath(location.pathname),
    teamManagement: isSupervisorTeamPath(location.pathname),
    trainingContent: isSupervisorTrainingPath(location.pathname),
    sessionCompliance: location.pathname === '/reports' || location.pathname.startsWith('/reports/'),
  });

  React.useEffect(() => {
    const path = location.pathname;
    setOpenGroups((prev) => {
      const next = { ...prev };
      if (isReportsPath(path)) next.reports = true;
      if (path.startsWith('/payroll') || path.startsWith('/leave/setup')) next.payroll = true;
      if (path.startsWith('/timesheets/inbox') || path.startsWith('/leave/inbox')) next.payrollApprovals = true;
      if (path === '/timesheets' || path === '/leave' || path.startsWith('/payroll/me')) next.myWork = true;
      if (isSupervisorMyClassesPath(path)) next.myClasses = true;
      if (isSupervisorFillReportsPath(path)) next.myMonthlyReports = true;
      if (isSupervisorReviewReportsPath(path)) next.teamMonthlyReports = true;
      if (isSupervisorApprovalsPath(path)) next.supervisorApprovals = true;
      if (isSupervisorTeamPath(path)) next.teamManagement = true;
      if (isSupervisorTrainingPath(path)) next.trainingContent = true;
      if (path === '/reports' || path.startsWith('/reports/')) next.sessionCompliance = true;
      return next;
    });
  }, [location.pathname]);

  const toggleGroup = (id) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const drawer = (
    <div>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 1.5,
          px: 2,
        }}
      >
        <BrandLogo height={56} />
      </Box>
      <Divider />
      <List>
        {navigationItems.map((item) => {
          if (item.type === 'item') {
            return (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={matchNavPath(location.pathname, item.path)}
                  onClick={() => handleNavigation(item.path)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                  {item.badge > 0 && <Chip size="small" color="primary" label={item.badge > 99 ? '99+' : item.badge} />}
                </ListItemButton>
              </ListItem>
            );
          }

          if (item.type === 'group') {
            const isOpen = openGroups[item.id] ?? false;
            return (
              <React.Fragment key={item.id}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => toggleGroup(item.id)}>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                    {isOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children?.map((child) => (
                      <ListItem key={child.text} disablePadding sx={{ pl: 4 }}>
                        <ListItemButton
                          selected={matchNavPath(location.pathname, child.path)}
                          onClick={() => handleNavigation(child.path)}
                        >
                          <ListItemIcon>{child.icon}</ListItemIcon>
                          <ListItemText
                            primary={child.text}
                            primaryTypographyProps={{ fontSize: '0.875rem', lineHeight: 1.3 }}
                          />
                          {child.badge > 0 && (
                            <Chip size="small" color="primary" label={child.badge > 99 ? '99+' : child.badge} />
                          )}
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          return null;
        })}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: supervisorPicker.isSupervisorPicker ? 0 : 1, mr: 2 }}>
            {resolveActiveNavItem(flatItems, location.pathname)?.text || 'Dashboard'}
          </Typography>

          {supervisorPicker.isSupervisorPicker ? (
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: 160, sm: 280 },
                flexGrow: 1,
                mr: 2,
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.8)' },
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                  '&:hover fieldset': { borderColor: 'white' },
                },
                '& .MuiSvgIcon-root': { color: 'white' },
              }}
            >
              <InputLabel id="layout-supervisor-teacher-label">Viewing schedule for</InputLabel>
              <Select
                labelId="layout-supervisor-teacher-label"
                label="Viewing schedule for"
                value={supervisorPicker.selectedTeacherId || ''}
                onChange={(e) => supervisorPicker.setSelectedTeacherId(e.target.value)}
                disabled={supervisorPicker.teachersLoading || supervisorPicker.teachers.length === 0}
              >
                {supervisorPicker.teachers.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}

          {/* Notifications */}
          <NotificationBell />

          {/* User Profile */}
          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{ ml: 1 }}
          >
            <Avatar sx={{ width: 32, height: 32 }}>
              {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: '64px', // AppBar height
        }}
      >
        {isSalesCountryScope() && !hasSalesSupervisorCountryAccess() ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No country is assigned for you. Please contact admin.
          </Alert>
        ) : null}
        <CallingHost />
        <Outlet />
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => handleNavigation('/profile')}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={() => handleNavigation('/profile?tab=password')}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          Change password
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};

const Layout = () => (
  <SupervisorTeacherProvider>
    <LayoutShell />
  </SupervisorTeacherProvider>
);

export default Layout; 